import {
  List,
  Datagrid,
  FunctionField,
  TextField,
  useRecordContext,
  useNotify,
  useRefresh,
  useGetList
} from 'react-admin';
import { useState } from 'react';
import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField as MuiTextField
} from '@mui/material';
import { API_BASE_URL, authFetch } from '../apiClient.ts';

type Suggestion = {
  id: number;
  contactId: number;
  contact?: { firstName: string; lastName: string } | null;
  groupId: number | null;
  group?: { name: string } | null;
  suggestedName: string | null;
  confidence: string | null;
  reasoning: string | null;
};

type GroupOption = { id: number; name: string };

const confidenceColor = (c: string | null) =>
  c === 'high' ? 'success' : c === 'medium' ? 'warning' : 'default';

const SuggestionActions = () => {
  const record = useRecordContext<Suggestion>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<GroupOption | null>(null);

  const { data: groups = [], isLoading: loadingGroups } = useGetList<GroupOption>('groups', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
    filter: { q: search }
  });

  if (!record) return null;

  const patch = async (action: 'approve' | 'reject', groupId?: number) => {
    setBusy(true);
    try {
      const response = await authFetch(
        `${API_BASE_URL}/group-contact-suggestions/${record.id}/${action}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: groupId ? JSON.stringify({ groupId }) : undefined
        }
      );
      if (!response.ok) {
        throw new Error('Request failed');
      }
      notify(action === 'approve' ? 'Added to group' : 'Suggestion rejected', {
        type: 'success'
      });
      refresh();
    } catch {
      notify('Action failed', { type: 'error' });
    } finally {
      setBusy(false);
      setPickerOpen(false);
    }
  };

  const handleApprove = () => {
    if (record.groupId) {
      patch('approve');
    } else {
      setPickerOpen(true);
    }
  };

  return (
    <Stack direction="row" spacing={1}>
      <Button size="small" variant="contained" disabled={busy} onClick={handleApprove}>
        Approve
      </Button>
      <Button size="small" color="error" disabled={busy} onClick={() => patch('reject')}>
        Reject
      </Button>

      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Select a group</DialogTitle>
        <DialogContent>
          <Autocomplete
            fullWidth
            options={groups}
            value={selectedGroup}
            onChange={(_, value) => setSelectedGroup(value)}
            onInputChange={(_, value) => setSearch(value)}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={loadingGroups}
            renderInput={(params) => (
              <MuiTextField
                {...params}
                label="Group"
                margin="normal"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingGroups ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={busy || !selectedGroup}
            onClick={() => selectedGroup && patch('approve', selectedGroup.id)}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

const GroupContactSuggestionList = () => (
  <List
    filter={{ status: 'PENDING' }}
    sort={{ field: 'createdAt', order: 'DESC' }}
    exporter={false}
    title="Group Suggestions"
  >
    <Datagrid bulkActionButtons={false}>
      <FunctionField<Suggestion>
        label="Contact"
        render={(record) =>
          record.contact
            ? `${record.contact.firstName} ${record.contact.lastName}`
            : `Contact #${record.contactId}`
        }
      />
      <FunctionField<Suggestion>
        label="Suggested group"
        render={(record) =>
          record.group?.name ??
          (record.suggestedName ? `${record.suggestedName} (needs group)` : '—')
        }
      />
      <FunctionField<Suggestion>
        label="Confidence"
        render={(record) => (
          <Chip
            size="small"
            label={record.confidence ?? 'low'}
            color={confidenceColor(record.confidence)}
          />
        )}
      />
      <TextField source="reasoning" />
      <FunctionField label="" render={() => <SuggestionActions />} />
    </Datagrid>
  </List>
);

export default GroupContactSuggestionList;
